import { createRoot } from 'react-dom/client';
import { App } from './App.tsx';
import { AppContextProvider } from './context/AppContextProvider.tsx';
import './index.css';
import { API_URL, WEBSITE_URL } from './env';
import { logger } from './lib/logger';
import { clearCachesAndReload } from './lib/clearCachesAndReload';

// Preconnect to API host to parallelize TLS handshake with module loading
const apiHost = import.meta.env.VITE_PUBLIC_API_HOST;
if (apiHost) {
  const link = document.createElement('link');
  link.rel = 'preconnect';
  link.href = API_URL;
  link.crossOrigin = 'use-credentials';
  document.head.appendChild(link);
}

const gitHash = import.meta.env.VITE_PUBLIC_GIT_HASH || 'unknown';

// Log environment variables
logger.info('[Main] AirCast started', {
  API_URL,
  WEBSITE_URL,
  environment: import.meta.env.MODE,
  version: import.meta.env.VITE_APP_VERSION || 'unknown',
  gitCommit: gitHash,
  envVars: {
    VITE_PUBLIC_API_HOST: import.meta.env.VITE_PUBLIC_API_HOST,
    VITE_PUBLIC_API_PORT: import.meta.env.VITE_PUBLIC_API_PORT,
    VITE_PUBLIC_WEBSITE_HOST: import.meta.env.VITE_PUBLIC_WEBSITE_HOST,
    VITE_PUBLIC_WEBSITE_PORT: import.meta.env.VITE_PUBLIC_WEBSITE_PORT,
  },
});

logger.info(`[Main] Git commit: ${gitHash.substring(0, 8)}`);

// Lazy-load observability — Faro and OpenTelemetry don't need to block first render
void import('./lib/faro').then(({ initFaro }) => {
  initFaro({
    url: import.meta.env.VITE_FARO_URL || undefined,
    appName: 'aircast-web',
    appVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
    environment: import.meta.env.MODE,
  });
});

void import('./lib/tracing').then(({ initTracer }) => {
  const shutdownTracer = initTracer({
    serviceName: 'aircast-web',
    serviceVersion: import.meta.env.VITE_APP_VERSION || 'unknown',
    exporterEndpoint: import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT || undefined,
    useConsoleExporter: !import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT,
  });

  window.addEventListener('beforeunload', () => {
    void shutdownTracer();
  });
});

// Auto-reload on stale chunk errors (e.g. after deployment changes chunk hashes)
window.addEventListener('vite:preloadError', (event) => {
  const reloadedAt = sessionStorage.getItem('chunk-reload');
  const now = Date.now();
  if (!reloadedAt || now - parseInt(reloadedAt, 10) > 10_000) {
    sessionStorage.setItem('chunk-reload', now.toString());
    event.preventDefault();
    clearCachesAndReload();
  }
});

// Global error handlers
window.addEventListener('error', (event) => {
  logger.captureError(event.error || new Error(event.message), {
    type: 'window.error',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener('unhandledrejection', (event) => {
  logger.captureError(new Error(event.reason?.message || 'Unhandled Promise Rejection'), {
    type: 'unhandledrejection',
    reason: event.reason,
  });
});

const rootElement = document.querySelector('#root')!;
const root = createRoot(rootElement);

function render(AppComponent: typeof App) {
  root.render(
    <AppContextProvider>
      <AppComponent />
    </AppContextProvider>,
  );
}

render(App);

if (import.meta.hot) {
  import.meta.hot.accept('./App.tsx', () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const NextApp = require('./App.tsx').App;

    render(NextApp);
  });
}
